import _, { groupBy } from 'lodash'
import { alphabeticalSort } from 'utils/alphabeticalSort'
import type {
  AnyNode,
  TActiveGateway,
  TActiveNode,
  TNodeLog,
  TNodeLures,
  TPlannedGateway,
  TPlannedNode,
  TPropertyEquipmentSummary,
} from '../../types'
import { TNodeStatus } from '../../types'
import { getIdentifier } from '../../utils/getIdentifier'
import { nodeBelongsToProperty } from '../../utils/nodeBelongsToProperty'
import type { TServiceCenterStore } from '../serviceCenterStore'

export const selectors = {
  getMapEquipmentStatuses: (s: TServiceCenterStore) => s.mapEquipmentStatuses,
  getMapEquipmentTypes: (s: TServiceCenterStore) => s.mapEquipmentTypes,
  getMapEquipmentDevices: (s: TServiceCenterStore) => s.mapEquipmentDevices,
  getAllPlannedNodes: (s: TServiceCenterStore) => s.plannedNodes,
  getAllActiveNodes: (s: TServiceCenterStore) => s.activeNodes,
  getAllPlannedAndActiveNodesForSelectedProperty: (
    s: TServiceCenterStore,
    selectedPropertyId: number | null,
  ) => [
    ...s.plannedNodes.filter((node) => selectedPropertyId === node.propertyId),
    ...s.activeNodes.filter((node) => nodeBelongsToProperty(node, selectedPropertyId)),
  ],
  getPlannedNodesForSelectedProperties: (s: TServiceCenterStore, selectedPropertyId: number | null) => {
    return s.plannedNodes.filter((node) => selectedPropertyId === node.propertyId)
  },
  getActiveNodesForSelectedProperties: (s: TServiceCenterStore, selectedPropertyId: number | null) => {
    if (!selectedPropertyId) return []

    return s.activeNodes.filter((node) => nodeBelongsToProperty(node, selectedPropertyId))
  },
  getSelectedEquipmentId: (s: TServiceCenterStore) =>
    s.selectedPlannedEquipmentIds[0] || s.selectedActiveEquipmentIds[0],
  getSelectedEquipmentNode: (s: TServiceCenterStore): AnyNode | null => {
    if (s.selectedPlannedEquipmentIds[0]) {
      return (
        s.plannedNodes.find((node) => (node as TPlannedNode).id === s.selectedPlannedEquipmentIds[0]) || null
      )
    } else if (s.selectedActiveEquipmentIds[0]) {
      return (
        s.activeNodes.find(
          (node) =>
            ((node as TActiveNode).nodeIdentifier || (node as TActiveGateway).gatewayIdentifier) ===
            s.selectedActiveEquipmentIds[0],
        ) || null
      )
    } else return null
  },
  getAllNodeLogs: (s: TServiceCenterStore) => s.nodeLogs,
  getSelectedEquipmentPrimaryLure: (s: TServiceCenterStore) => {
    const { trap } = selectors.getSelectedEquipmentNode(s) as TActiveNode
    const luresObj = selectors.getLures(s)

    if (!trap || !trap.lures?.active || _.isEmpty(trap.lures?.active)) return null

    const primaryLure = Object.values(trap.lures.active as unknown as TNodeLures).filter(
      (lure) => lure.primary,
    )[0]

    if (!primaryLure) return null

    const lureName = luresObj[primaryLure.lureId].lureName

    return { ...primaryLure, lureName }
  },
  getNodeStatuses: (s: TServiceCenterStore) => s.nodeStatuses,
  getAllServiceIssues: (s: TServiceCenterStore) => s.serviceIssues,
  getSelectedPropertyServiceIssues: (s: TServiceCenterStore, selectedPropertyId: number | null) => {
    if (!selectedPropertyId) return {}

    const filteredServiceIssues = Object.values(selectors.getAllServiceIssues(s))
      .flat()
      .filter((i) => i.propertyId === selectedPropertyId)

    const groupedServiceIssues = groupBy(filteredServiceIssues, 'nodeIdentifier')

    return groupedServiceIssues
  },
  getSelectedEquipmentLastLog: (s: TServiceCenterStore): TNodeLog | null => {
    if (s.selectedActiveEquipmentIds[0]) {
      return s.nodeLogs[s.selectedActiveEquipmentIds[0]] || null
    } else return null
  },
  getSelectedEquipmentServiceIssues: (s: TServiceCenterStore) => {
    if (s.selectedActiveEquipmentIds[0]) {
      return s.serviceIssues[s.selectedActiveEquipmentIds[0]] || []
    } else return []
  },
  getPropertySummaries: (s: TServiceCenterStore) => {
    const summaryData: Record<string, TPropertyEquipmentSummary> = {}

    const activeNodesByProperty: Record<number, Array<TActiveNode | TActiveGateway>> = groupBy(
      s.activeNodes,
      (n) =>
        (n as TActiveNode).propertyId ? (n as TActiveNode).propertyId : (n as TActiveGateway).propertyIds[0],
    )

    const plannedNodesByProperty: Record<number, Array<TPlannedNode | TPlannedGateway>> = groupBy(
      s.plannedNodes,
      'propertyId',
    )

    Object.keys(activeNodesByProperty).forEach((propertyId) => {
      if (!summaryData[propertyId]) {
        summaryData[propertyId] = {
          propertyId: +propertyId,
          onlineNodesCount: 0,
          offlineNodesCount: 0,
          errorNodesCount: 0,
          plannedNodesCount: 0,
        }
      }

      activeNodesByProperty[+propertyId].forEach((node) => {
        const nodeIdentifier = getIdentifier(node)
        const hasServiceIssues = (s.serviceIssues[nodeIdentifier]?.length ?? 0) > 0

        if (hasServiceIssues) {
          summaryData[propertyId].errorNodesCount += 1
        } else if (s.nodeStatuses[nodeIdentifier] === TNodeStatus.ACTIVE) {
          summaryData[propertyId].onlineNodesCount += 1
        } else {
          summaryData[propertyId].offlineNodesCount += 1
        }
      })
    })

    Object.keys(plannedNodesByProperty).forEach((propertyId) => {
      if (!summaryData[propertyId]) {
        summaryData[propertyId] = {
          propertyId: +propertyId,
          onlineNodesCount: 0,
          offlineNodesCount: 0,
          errorNodesCount: 0,
          plannedNodesCount: 0,
        }
      }

      summaryData[propertyId].plannedNodesCount = plannedNodesByProperty[+propertyId].length
    })

    return summaryData
  },
  getSwapReasonsBySelectedNodeType: (s: TServiceCenterStore, nodeType: string) => {
    if (!nodeType) return []

    if (Object.keys(s.swapReasons).includes(nodeType)) {
      return s.swapReasons[nodeType].sort((a, b) => {
        if (a.swapId === 'OTHER') return 1

        if (b.swapId === 'OTHER') return -1

        return alphabeticalSort(a.swapReason, b.swapReason)
      })
    }

    return []
  },
  getRemoveReasonsBySelectedNode: (s: TServiceCenterStore) => {
    const selectedNode = selectors.getSelectedEquipmentNode(s)
    const nodeType = (selectedNode as TActiveNode).nodeType || (selectedNode as TActiveGateway).gatewayType

    if (selectedNode && Object.keys(s.removeReasons).includes(nodeType))
      return s.removeReasons[nodeType].sort((a, b) => {
        if (a.removeId === 'OTHER') return 1

        if (b.removeId === 'OTHER') return -1

        return alphabeticalSort(a.removeReason, b.removeReason)
      })

    return []
  },
  getRemoveReasonsBySelectedNodeType: (s: TServiceCenterStore, nodeType: string) => {
    if (!nodeType) return []

    if (Object.keys(s.removeReasons).includes(nodeType))
      return s.removeReasons[nodeType].sort((a, b) => {
        if (a.removeId === 'OTHER') return 1

        if (b.removeId === 'OTHER') return -1

        return alphabeticalSort(a.removeReason, b.removeReason)
      })

    return []
  },
  getOfflineContentsLastDownloadTime: (s: TServiceCenterStore) => s.offlineContents.lastTimeDownloaded,
  getLures: (s: TServiceCenterStore) => s.lures,
  getLuresAsArray: (s: TServiceCenterStore) => {
    return Object.values(s.lures)
      .filter((lure) => lure.status === 'ACTIVE')
      .sort((a, b) => alphabeticalSort(a.lureName, b.lureName))
  },
}
